import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, IndianRupee, ChevronRight, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SubscribeButton from "@/components/locanest/SubscribeButton";
import ReferralCard from "@/components/locanest/ReferralCard";
import { useAuth } from "@/contexts/AuthContext";
const statusLabel = (b) => {
  if (b.status === "pending") return b.advance_enforced === false ? "Awaiting owner" : "Payment pending";
  return { advance_paid: "Reserved", completed: "Completed", rejected: "Rejected · Refunded", cancelled: "Cancelled" }[b.status] || b.status;
};
const STATUS_STYLE = {
  pending: "bg-[#F0EBE1] text-[#C85A32]",
  advance_paid: "bg-[#D4DEC6] text-[#2B3A67]",
  completed: "bg-[#D4DEC6] text-[#2B3A67]",
  rejected: "bg-red-50 text-red-600",
  cancelled: "bg-red-50 text-red-600",
};

export default function MyBookings() {
  const [items, setItems] = useState([]);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/bookings/mine")
      .then(({ data }) => setItems(Array.isArray(data) ? data : (data.items || [])))
      .finally(() => setLoading(false));
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const cancelPending = async (b) => {
    if (!window.confirm(`Cancel this booking?\n\n"${b.property_title}"\n\nThis will remove it from your list. Advance-paid bookings are non-refundable per policy; pending bookings simply close without any charge.`)) return;
    setCancellingId(b.id);
    try {
      await api.post(`/bookings/${b.id}/cancel`, { reason: "Tenant cancelled from bookings list" });
      setItems((prev) => prev.map((it) => (it.id === b.id ? { ...it, status: "cancelled" } : it)));
      toast.success("Booking cancelled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Your reservations</div>
      <h1 className="font-display text-4xl md:text-5xl mt-2">My bookings</h1>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {cfg?.subscription_enforcement && <SubscribeButton variant="compact" />}
        <span className="text-xs text-[#5C6B7A]">
          {cfg && !cfg.booking_advance_enforcement
            ? "Free launch — contact owners directly and complete deals through LocaNest for platform protection."
            : "Access unlocks owner contact across all listings. Booking still needs the per-deal advance."}
        </span>
      </div>

      <ReferralCard className="mt-6" />

      {user?.profile_completion && user.profile_completion.percent < 100 && (
        <Link to="/profile?section=details" className="mt-4 rounded-lg bg-[#F0EBE1] border border-[#2B3A67]/20 p-4 flex items-center justify-between gap-3 hover:border-[#C85A32]/50 transition-colors" data-testid="tenant-profile-nudge">
          <div className="text-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2B3A67] font-semibold mb-0.5">Complete your profile</div>
            Your profile is <strong>{user.profile_completion.percent}%</strong> complete{user.profile_completion.missing.length ? ` — add ${user.profile_completion.missing.join(", ")}` : ""}.
          </div>
          <div className="w-32 h-2 bg-[#FDFBF7] rounded-full overflow-hidden shrink-0">
            <div className="h-full bg-[#C85A32]" style={{ width: `${user.profile_completion.percent}%` }} />
          </div>
        </Link>
      )}

      <div className="mt-8 space-y-3" data-testid="my-bookings-list">
        {loading ? <div className="text-[#5C6B7A]">Loading…</div>
        : items.length === 0 ? (
          <div className="bg-[#F0EBE1] rounded-lg p-12 text-center text-[#5C6B7A]">
            No bookings yet. Reserve a property from listings to see it here.
          </div>
        ) : items.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-4 bg-[#F0EBE1] rounded-lg p-5 border border-[#1A1F2C]/10 hover:border-[#C85A32]/40 transition" data-testid={`booking-row-${b.id}`}>
            <Link to={`/bookings/${b.id}`} className="flex items-center justify-between gap-4 flex-1 min-w-0">
              <div className="min-w-0">
                <div className="font-display text-lg truncate">{b.property_title}</div>
                <div className="text-xs text-[#5C6B7A] mt-1">Agreement {b.agreement_number} · {new Date(b.created_at).toLocaleDateString()}</div>
                <div className="mt-2 flex items-center gap-3 text-sm">
                  {b.amount_paise > 0 ? (
                    <span className="inline-flex items-center gap-1"><IndianRupee size={13} />{(b.amount_paise / 100).toLocaleString("en-IN")}</span>
                  ) : (
                    <span className="text-xs text-[#2B3A67] font-medium">Free interest — no advance</span>
                  )}
                  {b.check_in && <span className="inline-flex items-center gap-1 text-[#5C6B7A]"><CalendarDays size={13} /> {b.check_in} → {b.check_out}</span>}
                </div>
              </div>
            </Link>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLE[b.status]}`}>{statusLabel(b)}</span>
              {b.status === "pending" && (
                <Button
                  onClick={() => cancelPending(b)}
                  disabled={cancellingId === b.id}
                  variant="outline"
                  size="sm"
                  className="rounded-md text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                  data-testid={`cancel-booking-${b.id}`}
                >
                  <XCircle size={13} className="mr-1" /> {cancellingId === b.id ? "Cancelling…" : "Cancel"}
                </Button>
              )}
              <Link to={`/bookings/${b.id}`} className="p-1" data-testid={`open-booking-${b.id}`}>
                <ChevronRight size={16} className="text-[#5C6B7A]" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
